import { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Plus, Save, Eye, ArrowLeft, Trash2, GripVertical } from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

interface SurveyField {
  id: string;
  type: 'text' | 'textarea' | 'number' | 'select' | 'radio' | 'checkbox' | 'date' | 'rating';
  label: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
  min?: number;
  max?: number;
  rows?: number;
}

export default function SurveyEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/surveys';
  const { user } = useAuthStore();
  const isEditing = id && id !== 'new';

  const [name, setName] = useState('');
  const [fields, setFields] = useState<SurveyField[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { isLoading } = useQuery({
    queryKey: ['survey-template', id],
    queryFn: async () => {
      if (!isEditing) return null;
      const templates = await api.getSurveyTemplates(user?.organization_id || '');
      const template = templates.find(t => t.id === id);
      if (template) {
        // Populate form state with existing data
        setName(template.name);
        const fields = template.schema?.fields;
        if (fields && Array.isArray(fields)) {
          setFields(fields as SurveyField[]);
        }
      }
      return template || null;
    },
    enabled: !!isEditing && !!user?.organization_id,
  });

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; fields: SurveyField[] }) => {
      console.log('Creating survey template:', data);
      return api.createSurveyTemplate({
        organization_id: user?.organization_id || '',
        name: data.name,
        description: undefined,
        schema: { fields: data.fields },
        ui_schema: {},
        settings: {},
        created_by: user?.id,
      });
    },
    onSuccess: (result) => {
      console.log('Survey template created:', result);
      navigate(returnTo);
    },
    onError: (error) => {
      console.error('Failed to create survey:', error);
      setError('Failed to save survey. Please try again.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: { name: string; fields: SurveyField[] }) => {
      if (!id) throw new Error('No template ID');
      return api.updateSurveyTemplate(id, {
        name: data.name,
        description: undefined,
        schema: { fields: data.fields },
      });
    },
    onSuccess: () => {
      navigate(returnTo);
    },
  });

  const handleSave = () => {
    setError(null);
    if (!name.trim()) {
      setError('Please enter a survey name');
      return;
    }
    if (fields.length === 0) {
      setError('Please add at least one field to your survey');
      return;
    }
    const data = { name: name.trim(), fields };
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const addField = (type: SurveyField['type']) => {
    const newField: SurveyField = {
      id: `field-${Date.now()}`,
      type,
      label: `New ${type} field`,
      required: false,
      placeholder: '',
      options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : undefined,
    };
    setFields([...fields, newField]);
    setSelectedFieldId(newField.id);
  };

  const updateField = (id: string, updates: Partial<SurveyField>) => {
    setFields(fields.map(f => f.id === id ? { ...f, ...updates } : f));
  };

  const deleteField = (id: string) => {
    setFields(fields.filter(f => f.id !== id));
    if (selectedFieldId === id) {
      setSelectedFieldId(null);
    }
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...fields];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    setFields(newFields);
  };

  const selectedField = fields.find(f => f.id === selectedFieldId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(returnTo)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <Input
              placeholder="Survey Name"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(null); }}
              className="text-xl font-bold h-auto py-1 px-0 border-0 focus-visible:ring-0"
            />
            <p className="text-muted-foreground text-sm">Build your survey</p>
            {error && <p className="text-sm text-destructive mt-1">{error}</p>}
          </div>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowPreview(true)}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending}>
            <Save className="w-4 h-4 mr-2" />
            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-6">
        <div className="col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Fields</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { label: 'Text', type: 'text' as const },
                { label: 'Textarea', type: 'textarea' as const },
                { label: 'Number', type: 'number' as const },
                { label: 'Select', type: 'select' as const },
                { label: 'Radio', type: 'radio' as const },
                { label: 'Checkbox', type: 'checkbox' as const },
                { label: 'Date', type: 'date' as const },
                { label: 'Rating', type: 'rating' as const },
              ].map((field) => (
                <Button
                  key={field.type}
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => addField(field.type)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {field.label}
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="col-span-2 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Survey Canvas</CardTitle>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg">
                  <p>Click on fields to add them to your survey</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <div
                      key={field.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedFieldId === field.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setSelectedFieldId(field.id)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <GripVertical className="w-4 h-4 text-muted-foreground" />
                          <Badge variant="secondary" className="text-xs">
                            {field.type}
                          </Badge>
                          {field.required && <Badge variant="destructive" className="text-xs">Required</Badge>}
                        </div>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); moveField(index, 'up'); }}
                            disabled={index === 0}
                          >
                            <ArrowLeft className="w-4 h-4 rotate-90" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => { e.stopPropagation(); moveField(index, 'down'); }}
                            disabled={index === fields.length - 1}
                          >
                            <ArrowLeft className="w-4 h-4 -rotate-90" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive"
                            onClick={(e) => { e.stopPropagation(); deleteField(field.id); }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="font-medium">{field.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="col-span-1">
          {selectedField ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Field Properties</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Label</Label>
                  <Input
                    value={selectedField.label}
                    onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={selectedField.required}
                      onChange={(e) => updateField(selectedField.id, { required: e.target.checked })}
                    />
                    Required
                  </Label>
                </div>
                {(selectedField.type === 'text' || selectedField.type === 'textarea') && (
                  <div className="space-y-2">
                    <Label>Placeholder</Label>
                    <Input
                      value={selectedField.placeholder || ''}
                      onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })}
                    />
                  </div>
                )}
                {selectedField.type === 'textarea' && (
                  <div className="space-y-2">
                    <Label>Rows</Label>
                    <Input
                      type="number"
                      min={2}
                      max={10}
                      value={selectedField.rows || 3}
                      onChange={(e) => updateField(selectedField.id, { rows: parseInt(e.target.value) })}
                    />
                  </div>
                )}
                {(selectedField.type === 'select' || selectedField.type === 'radio') && (
                  <div className="space-y-2">
                    <Label>Options (comma-separated)</Label>
                    <Textarea
                      value={selectedField.options?.join(', ') || ''}
                      onChange={(e) => updateField(selectedField.id, { 
                        options: e.target.value.split(',').map(o => o.trim()).filter(Boolean)
                      })}
                      placeholder="Option 1, Option 2, Option 3"
                    />
                  </div>
                )}
                {selectedField.type === 'number' && (
                  <>
                    <div className="space-y-2">
                      <Label>Min</Label>
                      <Input
                        type="number"
                        value={selectedField.min || ''}
                        onChange={(e) => updateField(selectedField.id, { min: parseInt(e.target.value) || undefined })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max</Label>
                      <Input
                        type="number"
                        value={selectedField.max || ''}
                        onChange={(e) => updateField(selectedField.id, { max: parseInt(e.target.value) || undefined })}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                Select a field to edit its properties
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Survey Preview</DialogTitle>
            <DialogDescription>
              Preview how your survey will look to respondents
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-6 p-4">
            {fields.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Add fields to see the preview
              </p>
            ) : (
              <div className="space-y-6">
                {fields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label>
                      {field.label}
                      {field.required && <span className="text-destructive ml-1">*</span>}
                    </Label>
                    {field.type === 'text' && (
                      <Input placeholder={field.placeholder} />
                    )}
                    {field.type === 'textarea' && (
                      <Textarea placeholder={field.placeholder} rows={field.rows} />
                    )}
                    {field.type === 'number' && (
                      <Input type="number" min={field.min} max={field.max} />
                    )}
                    {field.type === 'select' && (
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options?.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {field.type === 'radio' && (
                      <div className="space-y-2">
                        {field.options?.map((opt) => (
                          <label key={opt} className="flex items-center space-x-2">
                            <input type="radio" name={field.id} />
                            <span>{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    {field.type === 'checkbox' && (
                      <label className="flex items-center space-x-2">
                        <input type="checkbox" />
                        <span>{field.label}</span>
                      </label>
                    )}
                    {field.type === 'date' && (
                      <Input type="date" />
                    )}
                    {field.type === 'rating' && (
                      <div className="flex space-x-1">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Button key={n} variant="outline" className="w-10 h-10">{n}</Button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
